import React, {
  type ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';
import useDelayer from 'common/util/useDelayer';

import { forwardRefNoClassName } from './common';

export const textareaVariants = tv({
  slots: {
    base: [
      'box-border outline-none bg-transparent dark:bg-input/30 resize-none w-full',
      'placeholder:text-placeholder',
    ],
  },
  variants: {
    variant: {
      default: {
        base: 'input-border input-disabled',
      },
      unstyled: {
        base: 'bg-transparent dark:bg-transparent',
      },
    },
    size: {
      small: {
        base: 'min-h-7 px-2 py-1 text-base rounded-sm',
      },
      default: {
        base: 'min-h-8 px-3 py-1 text-base rounded-sm',
      },
      large: {
        base: 'min-h-11 px-4 py-1 text-lg rounded-sm',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'unstyled',
      class: {
        base: 'min-h-auto h-auto border-none p-0 rounded-none',
      },
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

export type TextareaSelectionDirection = 'forward' | 'backward' | 'none';

export type TextareaProps = ComponentPropsWithoutRef<'textarea'> &
  VariantProps<typeof textareaVariants> & {
    autoResize?: boolean;
    autoResizeMaxRows?: number;
  };

export interface TextareaRef {
  getTextarea: () => HTMLTextAreaElement | null;
  getValue: () => string;
  focus: () => void;
  getSelectionRange: () => { start: number; end: number };
  setValue: (value: string, viaTextEvent?: boolean) => void;
  setCursorPosition: (position: number) => void;
  setSelectionRange: (
    start: number,
    end: number,
    direction?: 'forward' | 'backward' | 'none',
  ) => void;
  insertText: (text: string) => void;
}

const Textarea = forwardRefNoClassName<TextareaRef, TextareaProps>(
  ({ rows = 1, autoResize, autoResizeMaxRows, variant, size, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const styles = textareaVariants({
      variant,
      size,
    });

    const resizeTextarea = useCallback(() => {
      const textarea = textareaRef.current;

      if (!textarea || !autoResize) {
        return;
      }

      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;

      if (autoResizeMaxRows) {
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight =
          Number.parseInt(computedStyle.lineHeight) ||
          Number.parseInt(computedStyle.fontSize) * 1.2;
        const maxHeight = lineHeight * autoResizeMaxRows;
        const finalHeight = Math.min(scrollHeight, maxHeight);

        textarea.style.height = `${finalHeight}px`;
        textarea.style.maxHeight = `${maxHeight}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      } else {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.maxHeight = 'none';
        textarea.style.overflowY = 'hidden';
      }
    }, [autoResize, autoResizeMaxRows]);

    const debouncedResize = useDelayer(resizeTextarea, 10);

    useEffect(() => {
      if (!autoResize) {
        return;
      }

      window.addEventListener('resize', debouncedResize);
      return () => {
        window.removeEventListener('resize', debouncedResize);
      };
    }, [autoResize, debouncedResize]);

    useLayoutEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, props.value, props.defaultValue]);

    useImperativeHandle(ref, () => ({
      getTextarea: () => textareaRef.current,
      getValue: () => textareaRef.current?.value || '',
      focus: () => textareaRef.current?.focus(),
      getSelectionRange: () => ({
        start: textareaRef.current?.selectionStart || 0,
        end: textareaRef.current?.selectionEnd || 0,
      }),
      setValue: (value: string) => {
        if (textareaRef.current) {
          textareaRef.current.value = value;
        }
      },
      setCursorPosition: (position: number) => {
        textareaRef.current?.setSelectionRange(position, position);
      },
      setSelectionRange: (
        start: number,
        end: number,
        direction: TextareaSelectionDirection = 'none',
      ) => {
        textareaRef.current?.setSelectionRange(start, end, direction);
      },
      insertText: (text: string) => {
        if (document.execCommand('insertText', false, text)) {
          return;
        }

        const textarea = textareaRef.current;
        if (!textarea) {
          return;
        }

        const selectionStart = textarea.selectionStart;
        const selectionEnd = textarea.selectionEnd;
        const value = textarea.value;
        const before = value.substring(0, selectionStart);
        const after = value.substring(selectionEnd);

        textarea.value = before + text + after;
        textarea.setSelectionRange(selectionStart + text.length, selectionStart + text.length);
        resizeTextarea();
      },
    }));

    return (
      <textarea
        ref={textareaRef}
        className={cn(styles.base())}
        rows={rows}
        {...props}
        onChange={(e) => {
          props.onChange?.(e);
          resizeTextarea();
        }}
      />
    );
  },
);

export default Textarea;
